import { intersection } from "lodash";
import { CommunicationChannelType, MessageFormatType, MessageTemplate, Plugin, SelectionState } from "types";
import { TravelerMessage } from "./TravelersSelector";

export interface DrawerFormState {
    email: { subject: string; body: string };
    sms: { body: string };
    channels: string[];
}

export interface MessageSendTo {
    id?: ID;
    type: "traveler" | "travelers" | "guides" | "organizer";
}

export interface OutgoingMessageFormat {
    type: MessageFormatType;
    body?: string;
    subject?: string;
    asset?: { id: string };
}

export interface OutgoingChannelTemplate {
    type: CommunicationChannelType | string;
    subject?: string;
    body: string;
    formats?: OutgoingMessageFormat[];
}

export interface OutgoingMessage {
    filters?: unknown;
    from: ID;
    to: MessageSendTo[];
    channels: string[];
    formats: OutgoingMessageFormat[];
    messageTemplate: {
        channels: OutgoingChannelTemplate[];
    };
}

export interface DrawerFormErrors {
    email?: { subject?: string; body?: string };
    sms?: { body?: string };
}

export interface ChannelConfig {
    id: string;
    label: string;
    supportsAttachments?: boolean;
    requiresSubject?: boolean;
    templateField: "email" | "sms";
}

export interface MessageDrawerChannels {
    isEmailAvailable?: boolean;
    isPhoneAvailable?: boolean;
    messagingPlugin?: Plugin;
    /** Channels pre-checked when the drawer opens. */
    preferred?: string[];
}

export interface ChannelAvailability {
    isEmailAvailable: boolean;
    isSmsAvailable: boolean;
    messagingPlugin?: Plugin;
}

export const buildChannelConfigs = ({
    isEmailAvailable,
    isSmsAvailable,
    messagingPlugin,
}: ChannelAvailability): ChannelConfig[] => {
    const configs: ChannelConfig[] = [];
    if (isSmsAvailable) {
        configs.push({ id: CommunicationChannelType.SMS, label: "SMS", templateField: "sms" });
    }
    if (isEmailAvailable) {
        configs.push({
            id: CommunicationChannelType.EMAIL,
            label: "Email",
            supportsAttachments: true,
            requiresSubject: true,
            templateField: "email",
        });
    }
    if (messagingPlugin) {
        configs.push({
            id: messagingPlugin.id,
            label: messagingPlugin.name,
            supportsAttachments: true,
            templateField: "sms",
        });
    }
    return configs;
};

/** Falls back to every available channel when none of the preferred ones can be used. */
export const getInitialChannelIds = (channelConfigs: ChannelConfig[], preferredChannels: string[] = []) => {
    const allIds = channelConfigs.map((config) => config.id);
    const preferredIds = intersection(allIds, preferredChannels);
    return preferredIds.length > 0 ? preferredIds : allIds;
};

export const partitionChannels = (channelConfigs: ChannelConfig[], selectedIds: string[]) => {
    const selected = channelConfigs.filter((config) => selectedIds.includes(config.id));
    const nonEmail = selected.filter((config) => config.id !== CommunicationChannelType.EMAIL);
    return {
        selected,
        plainText: nonEmail,
        smsChannels: nonEmail.filter((config) => !config.supportsAttachments),
        appChannels: nonEmail.filter((config) => !!config.supportsAttachments),
    };
};

interface RecipientsInput {
    travelersList?: TravelerMessage[];
    selectedTravelers: SelectionState;
    to?: MessageSendTo[];
}

export const buildRecipients = ({ travelersList, selectedTravelers, to }: RecipientsInput): MessageSendTo[] => {
    if (!travelersList) {
        return (to ?? [{ type: "travelers" }]).filter((recipient) => recipient.type !== "traveler" || !!recipient.id);
    }
    const ids = selectedTravelers.all ? travelersList.map((traveler) => traveler.id) : selectedTravelers.selected;
    return ids.filter((id): id is ID => !!id).map((id) => ({ id, type: "traveler" }));
};

// Matches legacy SmsForm tooltip + MessageModal validation (apps/seller/src/components/Common/MessageModal/MessageModal.tsx:304).
const SMS_MAX_LENGTH = 737;

export const validate = (
    values: DrawerFormState,
    template: MessageTemplate | null,
    channelConfigs: ChannelConfig[],
    hasAttachment = false,
) => {
    const errors: DrawerFormErrors = {};
    const { plainText, selected } = partitionChannels(channelConfigs, values.channels);
    const isEmailSelected = values.channels.includes(CommunicationChannelType.EMAIL);
    const needsSubject = selected.some((config) => config.requiresSubject);
    const templateHasSmsContent = !template || !!template.sms?.body;

    if (needsSubject && !values.email.subject) {
        errors.email = { ...errors.email, subject: "is required" };
    }
    if (isEmailSelected && !values.email.body && !hasAttachment) {
        errors.email = { ...errors.email, body: "is required" };
    }
    if (plainText.length > 0 && templateHasSmsContent) {
        const allPlainTextSupportAttachments = plainText.every((config) => config.supportsAttachments);
        if (!values.sms.body && !(allPlainTextSupportAttachments && hasAttachment)) {
            errors.sms = { body: "is required" };
        } else if (values.sms.body.length > SMS_MAX_LENGTH) {
            errors.sms = { body: `Must be less than ${SMS_MAX_LENGTH} characters` };
        }
    }

    return errors;
};

export const getFlashText = (selectedChannels: ChannelConfig[], isAllSelected: boolean) => {
    if (selectedChannels.length === 0) return "";

    const hasEmail = selectedChannels.some((config) => config.id === CommunicationChannelType.EMAIL);
    const hasSms = selectedChannels.some((config) => config.id === CommunicationChannelType.SMS);

    if (hasEmail && hasSms) {
        return isAllSelected
            ? "Message will be sent to guests who have provided valid contact information"
            : "Message will be sent to selected guest(s).";
    }

    if (hasEmail) {
        return isAllSelected
            ? "Email will be sent to guests who have provided a valid email address"
            : "Email will be sent to selected guest(s).";
    }

    if (hasSms) {
        return isAllSelected
            ? "SMS will be sent to guests who have provided a valid phone number"
            : "SMS will be sent to selected guest(s).";
    }

    // App channel (e.g. Airbnb)
    const label = selectedChannels[0].label;
    return isAllSelected
        ? `${label} message will be sent to guests who have provided valid contact information`
        : `${label} message will be sent to selected guest(s).`;
};
